import React, { useState } from "react";
import { Facebook, Instagram, Youtube, Twitter, Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { brand, footerLinks } from "../mock";
import { useToast } from "../hooks/use-toast";

const Footer = ({ onLinkClick }) => {
  const { toast } = useToast();
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast({ title: "Invalid email", description: "Please enter a valid email address" });
      return;
    }
    toast({ title: "You're in!", description: "Welcome to Inter Coastal Builders — 15% off is on its way." });
    setEmail("");
  };

  return (
    <footer className="bg-[color:var(--brand-ink)] text-neutral-300">
      {/* Newsletter */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="font-serif text-white text-2xl md:text-3xl">Get 15% Off Your First Order</div>
            <p className="mt-2 text-sm text-neutral-400 max-w-md">
              Join our list for insider deals, new arrivals, and design inspiration delivered straight to your inbox.
            </p>
          </div>
          <form onSubmit={subscribe} className="flex w-full">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white placeholder:text-neutral-400 outline-none focus:border-[color:var(--brand-red)] transition-colors rounded-l-sm"
            />
            <button className="bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red-dark)] text-white px-6 flex items-center gap-2 rounded-r-sm transition-colors">
              Sign Up <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>

      {/* Links */}
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
        {Object.entries(footerLinks).map(([group, items]) => (
          <div key={group}>
            <div className="text-white font-medium mb-4 text-sm tracking-wider uppercase">
              {group.replace(/_/g, " ")}
            </div>
            <ul className="space-y-2.5 text-sm">
              {items.map((l) => (
                <li key={l}>
                  <button
                    onClick={() => onLinkClick?.(l)}
                    className="text-left text-neutral-400 hover:text-white link-underline"
                  >
                    {l}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div>
          <div className="text-white font-medium mb-4 text-sm tracking-wider uppercase">Contact</div>
          <ul className="space-y-3 text-sm">
            <li>
              <a href={`tel:${brand.phone.replace(/[^0-9+]/g,"")}`} className="flex items-center gap-2 text-neutral-300 hover:text-white">
                <Phone className="w-4 h-4 text-[color:var(--brand-red)]" />
                <span className="font-medium">{brand.phone}</span>
              </a>
            </li>
            <li className="flex items-start gap-2 text-neutral-300">
              <MapPin className="w-4 h-4 mt-0.5 text-[color:var(--brand-red)]" />
              <span>101 Palm Harbor Pkwy Suite B-116,<br />Palm Coast, FL 32137</span>
            </li>
            <li className="flex items-center gap-2 text-neutral-400">
              <Mail className="w-4 h-4 text-[color:var(--brand-red)]" />
              support@intercoastalbuilders.com
            </li>
            <li className="text-neutral-400 leading-relaxed pt-2">
              Available Mon–Sat, 9AM–9PM EST for orders, questions, and design help.
            </li>
          </ul>

          <div className="mt-6 flex items-center gap-4">
            {[Facebook, Instagram, Youtube, Twitter].map((Icon, i) => (
              <a key={i} href="#" className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-[color:var(--brand-ink)] transition-colors">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} {brand.name}. All rights reserved.</div>
          <div className="flex items-center gap-5">
            {["Terms & Conditions", "Privacy Notice", "Accessibility", "Do Not Sell My Info"].map((l) => (
              <button key={l} onClick={() => onLinkClick?.(l)} className="hover:text-white">{l}</button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
